import React, { useState, useEffect, useRef } from 'react';
import {
  ChevronLeft, Settings, MessageSquare, Loader2,
  Phone, Video, Search, MoreVertical, Check, Users
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  useChatScreen,
  ChatRoom,
  Message as ChatMessage,
} from '@/stores/useChatScreen';
import { format } from 'date-fns';

import ChatList from '@/components/chat/ChatList';
import MessageItem from '@/components/chat/MessageItem';
import ChatInfoPanel from '@/components/chat/ChatInfoPanel';
import MessageInputField from '@/components/chat/MessageInputField';
import MessageRequestsPanel from '@/components/chat/MessageRequestsPanel';
import TypingIndicator from '@/components/chat/TypingIndicator';

// ─── Chat Header ────────────────────────────────────────────────────────────

const ChatHeader: React.FC<{
  room: ChatRoom;
  onBack: () => void;
  onToggleInfo: () => void;
  showChatInfo: boolean;
}> = ({ room, onBack, onToggleInfo, showChatInfo }) => (
  <div className="bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3 flex items-center justify-between shrink-0">
    <div className="flex items-center gap-3">
      <button
        onClick={onBack}
        className="lg:hidden p-2 rounded-lg hover:bg-light-border dark:hover:bg-dark-border transition text-light-subtle dark:text-dark-subtle"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>

      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary/20 to-golden/20 dark:from-primary/30 dark:to-golden/30 flex items-center justify-center overflow-hidden shrink-0">
        {room.room_avatar_url ? (
          <img src={room.room_avatar_url} alt={room.room_name} className="h-full w-full object-cover" />
        ) : (
          <span className="text-base font-bold text-primary dark:text-golden">
            {room.room_name.charAt(0).toUpperCase()}
          </span>
        )}
      </div>

      <div>
        <div className="flex items-center gap-2">
          <h3 className="font-bold text-light-text dark:text-dark-text leading-tight">{room.room_name}</h3>
          {room.is_group && (
            <span className="px-2 py-0.5 bg-primary/10 dark:bg-golden/10 text-primary dark:text-golden text-xs font-semibold rounded-full">
              Group
            </span>
          )}
          {room.property_id && (
            <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 text-xs font-semibold rounded-full">
              Property
            </span>
          )}
          {room.business_id && !room.property_id && (
            <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-300 text-xs font-semibold rounded-full">
              Business
            </span>
          )}
        </div>
        <p className="text-xs text-light-subtle dark:text-dark-subtle">
          {room.is_group
            ? `${room.participants.length} member${room.participants.length !== 1 ? 's' : ''}`
            : room.property_name
              ? `Unit ${room.unit_number ?? ''} · ${room.property_name}`
              : room.business_name ?? 'Direct message'}
        </p>
      </div>
    </div>

    <div className="flex items-center gap-1">
      {room.allow_calls && (
        <>
          <button className="p-2 rounded-lg hover:bg-light-border dark:hover:bg-dark-border transition text-light-subtle dark:text-dark-subtle" title="Voice call">
            <Phone className="h-5 w-5" />
          </button>
          {room.allow_group_calls && room.max_call_participants > 2 && (
            <button className="p-2 rounded-lg hover:bg-light-border dark:hover:bg-dark-border transition text-light-subtle dark:text-dark-subtle" title="Video call">
              <Video className="h-5 w-5" />
            </button>
          )}
        </>
      )}
      <button className="p-2 rounded-lg hover:bg-light-border dark:hover:bg-dark-border transition text-light-subtle dark:text-dark-subtle" title="Search in chat">
        <Search className="h-5 w-5" />
      </button>
      <button
        onClick={onToggleInfo}
        className={`p-2 rounded-lg transition ${showChatInfo
          ? 'bg-light-border dark:bg-dark-border text-light-text dark:text-dark-text'
          : 'hover:bg-light-border dark:hover:bg-dark-border text-light-subtle dark:text-dark-subtle'
        }`}
        title="Chat details"
      >
        <MoreVertical className="h-5 w-5" />
      </button>
    </div>
  </div>
);

// ─── Messages Area ───────────────────────────────────────────────────────────

const MessagesArea: React.FC<{
  messages: ChatMessage[];
  isLoading: boolean;
  isSending: boolean;
  currentUserId?: string;
  typingUsers?: string[];
  onDeleteMessage?: (id: string) => void;
  onReplyMessage?: (id: string) => void;
  onEditMessage?: (id: string) => void;
}> = ({ messages, isLoading, isSending, currentUserId, typingUsers = [], onDeleteMessage, onReplyMessage, onEditMessage }) => {
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isSending]);

  const grouped = messages.reduce((acc, msg) => {
    const key = new Date(msg.created_at).toDateString();
    (acc[key] ??= []).push(msg);
    return acc;
  }, {} as Record<string, ChatMessage[]>);

  if (isLoading && messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary dark:text-golden" />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-2 text-center px-8">
        <MessageSquare className="h-12 w-12 text-light-subtle dark:text-dark-subtle opacity-30" />
        <p className="font-semibold text-light-text dark:text-dark-text">No messages yet</p>
        <p className="text-sm text-light-subtle dark:text-dark-subtle">Send the first message to start the conversation</p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 py-2 space-y-1">
      {Object.entries(grouped).map(([date, dateMessages]) => (
        <div key={date}>
          {/* Date separator */}
          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-light-border dark:bg-dark-border" />
            <span className="px-3 py-1 bg-light-border dark:bg-dark-border text-light-subtle dark:text-dark-subtle text-xs rounded-full whitespace-nowrap">
              {format(new Date(date), 'EEEE, MMMM d, yyyy')}
            </span>
            <div className="flex-1 h-px bg-light-border dark:bg-dark-border" />
          </div>

          {dateMessages.map((msg, idx) => {
            const prevMsg = dateMessages[idx - 1];
            const showSenderInfo = !prevMsg || prevMsg.sender_id !== msg.sender_id;
            return (
              <MessageItem
                key={msg.id}
                id={msg.id}
                content={msg.content}
                sender_id={msg.sender_id}
                current_user_id={currentUserId ?? ''}
                sender_name={msg.profile?.full_name ?? 'Unknown'}
                sender_avatar={msg.profile?.avatar_url}
                created_at={msg.created_at}
                media_url={msg.media_url}
                media_type={msg.media_type as any}
                is_system_message={msg.is_system_message}
                onDelete={onDeleteMessage}
                onReply={onReplyMessage}
                onEdit={onEditMessage}
                showSenderInfo={showSenderInfo}
              />
            );
          })}
        </div>
      ))}

      {typingUsers.length > 0 && <TypingIndicator users={typingUsers} />}

      {isSending && (
        <div className="flex justify-end pr-2 pb-1">
          <Loader2 className="h-4 w-4 animate-spin text-light-subtle dark:text-dark-subtle" />
        </div>
      )}

      <div ref={endRef} />
    </div>
  );
};

// ─── Empty State ─────────────────────────────────────────────────────────────

const EmptyState: React.FC<{ requestCount: number; onViewRequests: () => void }> = ({ requestCount, onViewRequests }) => (
  <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
    <div className="h-20 w-20 bg-primary/10 dark:bg-golden/10 rounded-full flex items-center justify-center mb-5">
      <MessageSquare className="h-10 w-10 text-primary dark:text-golden" />
    </div>
    <h2 className="text-xl font-bold text-light-text dark:text-dark-text">Welcome to Messages</h2>
    <p className="text-light-subtle dark:text-dark-subtle max-w-xs mt-2 text-sm">
      Select a conversation from the sidebar to start chatting.
    </p>
    {requestCount > 0 && (
      <button
        onClick={onViewRequests}
        className="mt-5 px-5 py-2 bg-primary dark:bg-golden text-white rounded-xl text-sm font-semibold hover:opacity-90 transition"
      >
        View {requestCount} message request{requestCount !== 1 ? 's' : ''}
      </button>
    )}
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const MessagesPage: React.FC = () => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [showChatInfo, setShowChatInfo] = useState(false);
  const [showRequests, setShowRequests] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const {
    chatRooms,
    activeRoom,
    messages,
    messageRequests,
    roomsLoading,
    messagesLoading,
    requestsLoading,
    sendingMessage,
    uploadingMedia,
    fetchChatRooms,
    fetchMessages,
    fetchMessageRequests,
    sendMessage,
    sendMediaMessage,
    respondToMessageRequest,
    setActiveRoom,
    clearErrors,
  } = useChatScreen();

  // Load on mount
  useEffect(() => {
    Promise.all([fetchChatRooms(), fetchMessageRequests()]).catch(console.error);
    return () => clearErrors();
  }, []);

  // Load messages when room changes
  useEffect(() => {
    if (activeRoom) {
      fetchMessages(activeRoom.room_id);
      setShowChatInfo(false);
    }
  }, [activeRoom?.room_id]);

  const handleSendMessage = async () => {
    if (!activeRoom || !inputValue.trim()) return;
    const content = inputValue.trim();
    setInputValue('');
    await sendMessage(activeRoom.room_id, content);
  };

  const handleSendMedia = async (file: File) => {
    if (!activeRoom) return;
    await sendMediaMessage(activeRoom.room_id, file);
  };

  const handleAcceptRequest = async (id: string) => {
    await respondToMessageRequest(id, 'accept');
    await fetchChatRooms();
  };

  const handleDeclineRequest = async (id: string) => {
    await respondToMessageRequest(id, 'decline');
  };

  const handleCopyInviteLink = () => {
    if (!activeRoom?.is_group) return;
    navigator.clipboard.writeText(`${window.location.origin}/join-group/${activeRoom.room_id}`);
    setCopyFeedback(true);
    setTimeout(() => setCopyFeedback(false), 2000);
  };

  const handleSelectRoom = (room: ChatRoom) => {
    setActiveRoom(room);
    setShowRequests(false);
    setInputValue('');
  };

  return (
    <div className="flex h-screen bg-light-bg dark:bg-dark-bg overflow-hidden">

      {/* ── Sidebar ── */}
      <div className={`${activeRoom ? 'hidden lg:flex' : 'flex'} flex-col w-full lg:w-[360px] border-r border-light-border dark:border-dark-border h-full shrink-0`}>

        {/* Sidebar header */}
        <div className="px-4 pt-4 pb-2 border-b border-light-border dark:border-dark-border bg-light-card dark:bg-dark-card shrink-0">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-xl font-bold text-light-text dark:text-dark-text">Messages</h1>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setShowRequests(v => !v)}
                className="relative p-2 rounded-lg hover:bg-light-border dark:hover:bg-dark-border transition text-light-subtle dark:text-dark-subtle"
                title="Message requests"
              >
                <Users className="h-5 w-5" />
                {messageRequests.length > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-danger text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {messageRequests.length > 9 ? '9+' : messageRequests.length}
                  </span>
                )}
              </button>
              <button className="p-2 rounded-lg hover:bg-light-border dark:hover:bg-dark-border transition text-light-subtle dark:text-dark-subtle" title="Settings">
                <Settings className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Message requests panel */}
        {showRequests && (
          <div className="border-b border-light-border dark:border-dark-border bg-light-card dark:bg-dark-card px-4 py-3 max-h-72 overflow-y-auto shrink-0">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-light-text dark:text-dark-text">Message Requests</h3>
              <button onClick={() => setShowRequests(false)} className="text-xs text-light-subtle dark:text-dark-subtle hover:text-light-text dark:hover:text-dark-text">
                Close
              </button>
            </div>
            <MessageRequestsPanel
              requests={messageRequests}
              onAcceptRequest={handleAcceptRequest}
              onDeclineRequest={handleDeclineRequest}
              isLoading={requestsLoading}
            />
          </div>
        )}

        {/* Chat list */}
        <div className="flex-1 overflow-hidden">
          <ChatList
            rooms={chatRooms}
            activeRoomId={activeRoom?.room_id}
            isLoading={roomsLoading}
            onSelectRoom={handleSelectRoom}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            currentUserId={user?.id}
          />
        </div>

        {/* Current user footer */}
        <div className="px-4 py-3 border-t border-light-border dark:border-dark-border bg-light-card dark:bg-dark-card shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-golden text-white flex items-center justify-center text-sm font-bold shrink-0">
              {user?.email?.charAt(0).toUpperCase() ?? 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-light-text dark:text-dark-text truncate">{user?.email ?? 'User'}</p>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                <span className="text-xs text-green-500 font-medium">Online</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Main chat area ── */}
      <div className={`${!activeRoom ? 'hidden lg:flex' : 'flex'} flex-1 flex-col h-full overflow-hidden`}>
        {activeRoom ? (
          <>
            <ChatHeader
              room={activeRoom}
              onBack={() => setActiveRoom(null)}
              onToggleInfo={() => setShowChatInfo(v => !v)}
              showChatInfo={showChatInfo}
            />

            <div className="flex flex-1 overflow-hidden">
              <div className="flex flex-col flex-1 overflow-hidden">
                <MessagesArea
                  messages={messages}
                  isLoading={messagesLoading}
                  isSending={sendingMessage || uploadingMedia}
                  currentUserId={user?.id}
                  typingUsers={[]}
                />

                <MessageInputField
                  value={inputValue}
                  onChange={setInputValue}
                  onSendMessage={handleSendMessage}
                  onSendMedia={handleSendMedia}
                  isLoading={sendingMessage}
                  isUploading={uploadingMedia}
                  disabled={!activeRoom}
                />
              </div>

              {/* Info panel */}
              {showChatInfo && (
                <div className="w-72 shrink-0 border-l border-light-border dark:border-dark-border hidden xl:block h-full overflow-y-auto">
                  <ChatInfoPanel
                    roomName={activeRoom.room_name}
                    roomAvatar={activeRoom.room_avatar_url}
                    participantCount={activeRoom.participants.length}
                    isGroup={activeRoom.is_group}
                    createdAt={activeRoom.created_at}
                    businessName={activeRoom.business_name}
                    propertyName={activeRoom.property_name}
                    members={activeRoom.participants}
                    onClose={() => setShowChatInfo(false)}
                    onSearchInChat={() => {}}
                    onViewMedia={() => {}}
                    onShowMembers={() => {}}
                    onCopyInviteLink={handleCopyInviteLink}
                    onMuteNotifications={() => {}}
                    onLeaveGroup={() => {}}
                    isEncrypted={false}
                  />
                </div>
              )}
            </div>
          </>
        ) : (
          <EmptyState
            requestCount={messageRequests.length}
            onViewRequests={() => setShowRequests(true)}
          />
        )}
      </div>

      {/* Copy feedback toast */}
      {copyFeedback && (
        <div className="fixed bottom-5 right-5 px-4 py-2.5 bg-green-500 text-white rounded-xl flex items-center gap-2 shadow-lg animate-fade-in z-50">
          <Check className="h-4 w-4" />
          <span className="text-sm font-medium">Invite link copied!</span>
        </div>
      )}
    </div>
  );
};

export default MessagesPage;
